import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { collection, deleteDoc, doc, DocumentData, DocumentReference, Firestore, getDoc, onSnapshot, query, setDoc, updateDoc, where } from '@angular/fire/firestore';
import { getStorage, ref, UploadMetadata, uploadBytesResumable, UploadTask } from 'firebase/storage';
import { lastValueFrom, Observable, throwError } from 'rxjs';
import { IGroupedCard } from 'src/app/models/groupedCard';
import { renderFrames } from 'src/app/shared/feature-flags';
import { CardsService } from 'src/app/shared/providers/cards.service';
import { FirebaseAuthService } from 'src/app/shared/providers/firebase-auth.service';
import { SettingsService } from 'src/app/shared/providers/settings.service';
import { environment } from 'src/environments/environment';
import { MockRenderBackend, RenderBackendKind, RenderJobFacts, RenderRunEvent, RenderUploadEvent } from './render-backend';
import { renderEtaFrames, renderEtaNumber, RenderEtaReading, RenderEtaSide } from './render-eta';
import {
  RenderCheckoutError,
  RenderCheckoutSession,
  RenderEntitlement,
  RenderFailureBlame,
  RenderFlowStep,
  RenderJob,
  RenderJobSource,
  RenderModelGoneError,
  RenderPackFiles,
  RenderPackSizes,
  renderPreviewFloor,
  renderPreviewStage,
  RenderPreviewResult,
  RenderRunError,
  RenderSidePhase,
  RenderSides,
  RenderVideoPhase,
  RenderVideoSide,
  renderVideosProgress,
  RenderVideosResult,
  RENDER_VIDEO_LINES,
  renderVideosStatus,
} from './render-flow.model';
import { fileExtension, RenderUploadModel } from './render-upload.inspector';
import { RENDER_PREVIEW_STAGES, renderFileStem, renderPackCell, RenderPackKey } from './render.data';

// The flow on the render backend, for real (feature flag `renderLive`; owner, 2026-10-01). The files
// go where the library's go (the app's bucket, models-and-scenes-dev / -prod: a model file arriving
// there starts two GPU jobs), the model gets its Firestore document (userdata/{uid}/models/{id},
// FirebaseAuthService.patchDefaultModelInFirestore) and the page follows the fields the renderer
// writes into it. The preview is the renderer's preview-only run (Yahor, 2026-09-30):
// `loadStatus*` = `previewOnlyFinished`, the two frames in `previewOnlyPoster*`. The $9 is Stripe's
// own checkout page (purchases-back `create-checkout-session-model-unlock`, Yahor 2026-10-14):
// Stripe's webhook starts the videos on the backend (Pub/Sub → web-backend `model_unlock_notify`),
// and the page follows them — members pay too (owner, 2026-10-14). The platform pack is web-backend's
// ZIP (render_download, Yahor 2026-10-27). Samples stay the mock's (the parent class). See
// docs/render-landing-port.md.
//
// After the upload the page only reads: it never starts, restarts or resets a render of its own
// (owner, 2026-10-20: "мы закинули и ждём"). The backend runs what it was sent; a run that fails
// stays failed on the page, and "Try again" means uploading the model again.
//
// The user's render-flow models are the documents marked `renderProduct` (the rest of the collection
// is the library's own uploads). Next to the renderer's fields each keeps what the flow needs on
// any tab or device: `renderName`, `renderSource` (the file summary), `renderCreatedAt`,
// `renderEntitlement` ('preview' | 'full'), `renderSeenStep` (the step the user last looked at),
// `renderModelFolder` (the model file's folder in the bucket, which a re-render names),
// `renderCheckoutSession` / `renderCheckoutAt` (the Stripe checkout last opened for it),
// `renderPaidSession` (the checkout Stripe sent the buyer back from: paid),
// `renderVideosRequested` (when the page asked for the videos) and `renderStalled` (the run the
// page gave up on when the renderer went silent).

// The renderer writes one status and one progress per platform: Web renders the landscape frame and
// video, Mobile the portrait ones.
type Platform = 'Web' | 'Mobile';
const PLATFORMS: ReadonlyArray<Platform> = ['Web', 'Mobile'];

type ModelDocument = DocumentData;

// The preview-only run, orientation by orientation: each GPU job's status as the page shows it
// (measured on dev 2026-10-01: processStarted → modelDownload → rawThumbnailRendered →
// thumbnailUploaded → previewOnlyFinished, ~18 s a step). Statuses are compared lower-cased, as
// UploadFileService does; anything else before the frame counts as waiting for a GPU.
// `previewReady` is the simulator's, on its older documents. The percentage the renderer writes
// beside them is the full render's (it sits at 11 through the whole preview), so the page goes by
// the statuses alone.
const PREVIEW_PHASES: Readonly<Record<string, RenderSidePhase>> = {
  podstarted: 'loading',
  modeldownload: 'loading',
  rawthumbnailrendered: 'rendering',
  thumbnailrendered: 'rendering',
  thumbnailuploaded: 'saving',
  previewonlyfinished: 'done',
  previewready: 'done',
};
// The full run, video by video (measured on dev 2026-10-21): it passes the frames' statuses again
// within ~25 s, its progress at 11, then renders its 900 frames still at `thumbnailUploaded` while
// the progress climbs 11 → 100, puts the video together (`rawVideoRendered`, `videoRendered`) and
// ends at `finished` with the video written. Anything else — the preview's own end, `videoRequested`,
// `inQueue` — is a video not begun yet.
const VIDEO_PREPARING = ['podstarted', 'modeldownload', 'rawthumbnailrendered', 'thumbnailrendered', 'thumbnailuploaded'];
const VIDEO_FINISHING = ['rawvideorendered', 'videorendered', 'finished'];
// The renderer's progress once the scene is set up: frames from here on.
const VIDEO_FRAMES_FROM = 11;
// `inqueue` and `podstarted` belong to either run: once the frames exist, they are the videos'.
const VIDEO_UNDER_WAY = ['videorequested', 'rawvideorendered', 'videorendered'];
// A platform whose run has its frames behind it: its plain posters (`cardPoster*`) are final.
const PAST_THE_FRAMES = ['previewonlyfinished', 'previewready', ...VIDEO_UNDER_WAY, 'finished'];
// Where a finished preview stands. After a checkout, any other status is the paid run under way:
// Stripe's webhook started it, also when the buyer came back from Stripe on another address (its
// return goes to the dev site, whichever address the checkout was opened from).
const PREVIEW_END = ['previewonlyfinished', 'previewready'];

// How long a run may go without a word from the renderer before the page calls it stopped: the
// preview takes about two minutes (measured on dev, 2026-10-01), the videos far longer, and a Spot VM
// restart can leave them silent for 40 minutes and then go on (measured 2026-11-04; owner: 60).
const PREVIEW_STALL_MS = 10 * 60 * 1000;
const VIDEO_STALL_MS = 60 * 60 * 1000;
const STALL_CHECK_MS = 15 * 1000;
// Checks this far apart mean the page itself was asleep: a closed laptop, a frozen tab (a hidden one
// still checks once a minute). It heard nothing, which says nothing about the renderer. On 2026-11-04 a
// tab woke after an hour and wrote `renderStalled` over a run that had not paused once.
const STALL_ASLEEP_MS = 5 * 60 * 1000;

// A run the page gave up on: where the document stood then (runSignature), and after how long.
interface StalledRun {
  signature: string;
  minutes: number;
}

type FollowStep<T> = { progress: number; statusText: string; sides?: RenderSides; eta?: RenderEtaReading } | { done: T } | { error: RenderFailure };

// A failure status, in the page's words, and whose failure it is.
interface RenderFailure {
  text: string;
  blame: RenderFailureBlame;
  about?: 'textures';
}

@Injectable()
export class FirestoreRenderBackend extends MockRenderBackend {
  public override readonly kind: RenderBackendKind = 'simulator';

  // Each upload's stamped model path, from uploadId until its upload starts.
  private readonly modelPaths = new WeakMap<RenderUploadModel, string>();

  constructor(
    private readonly firestore: Firestore,
    private readonly firebaseAuth: FirebaseAuthService,
    private readonly cards: CardsService,
    private readonly settings: SettingsService,
    private readonly http: HttpClient,
    private readonly zone: NgZone,
  ) {
    super();
  }

  // The model's document id is its file's stamped name, so it is known before the first byte.
  public override uploadId(upload: RenderUploadModel): string {
    const modelPath = this.modelPaths.get(upload) || stampModelPath(upload.model.path);
    this.modelPaths.set(upload, modelPath);
    return baseName(modelPath);
  }

  // The library's upload (WebUploadService): every file of the drop under one folder,
  // uploaded_models/{uid}/{ts_rand}/, in the app's bucket, the model's name stamped with the time
  // and its metadata the library's. Textures first and the model last: the backend starts when the
  // model file arrives, and a model whose textures are still on their way renders without them. Then
  // the model's Firestore document, as the library creates it after an upload.
  public override uploadModel(upload: RenderUploadModel): Observable<RenderUploadEvent> {
    return new Observable<RenderUploadEvent>((subscriber) => {
      const uid = this.firebaseAuth.currentUser?.uid;
      if (!uid) {
        subscriber.error(new Error('Sign in to upload a model.'));
        return undefined;
      }

      const storage = getStorage();
      const folder = `uploaded_models/${uid}/${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
      // The name uploadId gave the page; a later upload of the same files gets a new one.
      const modelPath = this.modelPaths.get(upload) || stampModelPath(upload.model.path);
      this.modelPaths.delete(upload);
      const modelFolder = parentFolder(`${folder}/${modelPath}`);
      const backendId = baseName(modelPath);
      const totalBytes = [upload.model, ...upload.textures].reduce((sum, item) => sum + item.file.size, 0);
      const sent = new Map<string, number>();
      const tasks: UploadTask[] = [];
      const reference = doc(this.firestore, `userdata/${uid}/models/${backendId}`);
      let closed = false;
      // The model's last byte is in (its renderer starts on it), and the upload has ended: told the
      // page it is done, or why it failed.
      let modelSent = false;
      let settled = false;

      const put = (file: File, path: string, metadata?: UploadMetadata): Promise<void> =>
        new Promise((resolve, reject) => {
          const task = uploadBytesResumable(ref(storage, `${folder}/${path}`), file, metadata);
          tasks.push(task);
          task.on(
            'state_changed',
            (snapshot) => {
              sent.set(path, snapshot.bytesTransferred);
              const sentBytes = Math.min(totalBytes, [...sent.values()].reduce((sum, bytes) => sum + bytes, 0));
              this.zone.run(() => subscriber.next({ type: 'progress', sentBytes, totalBytes }));
            },
            reject,
            () => resolve(),
          );
        });

      const work = (async () => {
        // The textures only: the backend writes any other file it is sent (not a model, not an
        // image) into the user's library as a model it cannot render (`unprocessableFileType`).
        await Promise.all(upload.textures.map((item) => put(item.file, item.path)));
        // `preview_only` makes the renderer stop after the two frames (Yahor, Slack 2026-10-01); the
        // backend flags the same file `full_render_preview_only` once it is paid for. The library's
        // frame count stays: the paid run renders the videos from this same file. A count set by
        // hand in localStorage (`frameNumber`) wins over the `renderFrames` default, so a tester
        // can ask for another length (owner, 2026-10-20).
        const framesToRender = this.settings.getStoredFrameNumber() ?? renderFrames;
        await put(upload.model.file, modelPath, { customMetadata: { preview_only: 'true', framesToRender: framesToRender ? String(framesToRender) : this.settings.frameNumberToRender } });
        modelSent = true;
        if (closed) {
          return;
        }
        const existing = await getDoc(reference);
        await this.firebaseAuth.patchDefaultModelInFirestore({ fileName: backendId } as IGroupedCard, uid, existing.exists() ? existing.data() : undefined);
        const source: RenderJobSource = {
          kind: 'upload',
          fileName: upload.fileName,
          format: upload.format,
          fileCount: upload.fileCount,
          textureCount: upload.textures.length,
          totalBytes: upload.totalBytes,
        };
        if (closed) {
          return;
        }
        await setDoc(reference, {
          renderProduct: true,
          renderName: upload.name,
          renderSource: source,
          renderCreatedAt: Date.now(),
          renderEntitlement: 'preview',
          renderModelFolder: modelFolder,
        }, { merge: true });
        if (closed) {
          return;
        }
        settled = true;
        this.zone.run(() => {
          subscriber.next({ type: 'done', backendId });
          subscriber.complete();
        });
      })();
      work.catch((error) => {
        if (!closed) {
          settled = true;
          console.error('Render flow: upload failed', error);
          // A refusal (Storage or Firestore rules, a quota) says its code, so whoever sees it can
          // pass it on; a lost connection is just that.
          const code = (error as { code?: string })?.code;
          const text = !code || code === 'storage/retry-limit-exceeded'
            ? 'The connection dropped before the whole model arrived. Try again.'
            : `The server refused the upload (${code}). Try again, or contact support if it happens again.`;
          this.zone.run(() => subscriber.error(new Error(text)));
        }
      });

      return () => {
        closed = true;
        tasks.forEach((task) => task.cancel());
        // Stopped by the user after the model's last byte (owner, 2026-10-23: an upload has to be
        // stoppable): its renderer has started on it. Stop that, and take back the model's document
        // if it was written meanwhile, so the model does not come back to the library.
        if (modelSent && !settled) {
          this.cards.stopRenderPostRequest(backendId).catch((error) => console.warn('Render flow: stop_render failed', error));
          work
            .catch(() => undefined)
            .then(() => deleteDoc(reference))
            .catch((error) => console.warn('Render flow: could not remove the stopped upload', error));
        }
      };
    });
  }

  // The upload itself starts the preview on the backend (the file arriving in the bucket): the page
  // follows it, fresh or after a reload alike, and asks for nothing.
  public override runPreview(job: RenderJob, upload: RenderUploadModel | null): Observable<RenderRunEvent<RenderPreviewResult>> {
    if (job.source.kind === 'sample') {
      return super.runPreview(job, upload);
    }
    if (job.source.kind !== 'upload' || !job.backendId) {
      return throwError(() => new Error('The upload did not finish. Upload the model again.'));
    }
    return this.follow(job.backendId, PREVIEW_STALL_MS, (data) => readPreview(data));
  }

  public override runFullRender(job: RenderJob, resume = false): Observable<RenderRunEvent<RenderVideosResult>> {
    if (job.source.kind === 'sample') {
      return super.runFullRender(job);
    }
    if (job.source.kind !== 'upload' || !job.backendId) {
      return throwError(() => new Error('This model is no longer on the server. Upload it again.'));
    }
    return this.follow(job.backendId, VIDEO_STALL_MS, (data) => readVideos(data), resume ? undefined : (reference, data) => this.requestVideos(reference, data));
  }

  // Stripe's checkout page for the model's $9 (the user's Stripe customer, the backend's price),
  // remembered on the model so a tab without the job can tell whose return it is.
  public override async createCheckout(job: RenderJob): Promise<RenderCheckoutSession> {
    if (job.source.kind !== 'upload' || !job.backendId) {
      return super.createCheckout(job);
    }
    const uid = await this.firebaseAuth.getUserUid();
    let answer: { sessionId?: string; url?: string } | null = null;
    try {
      answer = await lastValueFrom(this.http.post<{ sessionId?: string; url?: string } | null>(
        `${environment.endpoints.purchaseWeb}/create-checkout-session-model-unlock`,
        { user_id: uid, model_id: job.backendId },
        await this.backendOptions(),
      ));
    } catch (error) {
      console.error('Render flow: create-checkout-session-model-unlock failed', error);
      throw new RenderCheckoutError(`We couldn’t open the Stripe checkout${httpCode(error)}. Try again, or contact support if it happens again.`);
    }
    if (!answer?.sessionId || !answer.url) {
      console.error('Render flow: the checkout came back without a session', answer);
      throw new RenderCheckoutError('We couldn’t open the Stripe checkout. Try again, or contact support if it happens again.');
    }
    // Without it only this tab knows the session (the job keeps it): no reason to stay.
    await updateDoc(await this.modelDocument(job.backendId), { renderCheckoutSession: answer.sessionId, renderCheckoutAt: Date.now() })
      .catch((error) => console.warn('Render flow: could not store the checkout on the model', error));
    return { sessionId: answer.sessionId, url: answer.url };
  }

  // Stripe sends the buyer back with the session (?session_id=cs_…) only once the card is
  // charged, and its webhook starts the videos: the page records the payment on the model and
  // follows them. Without the session (a reload of the confirming page) the model says it: paid
  // already, or its paid run under way; until then the payment is pending.
  public override async confirmPayment(job: RenderJob, sessionId: string): Promise<RenderEntitlement> {
    if (job.source.kind !== 'upload' || !job.backendId) {
      return super.confirmPayment(job, sessionId);
    }
    const reference = await this.modelDocument(job.backendId);
    const data = (await getDoc(reference)).data();
    if (!data) {
      throw new RenderModelGoneError();
    }
    const paid = sessionId.startsWith('cs_') ? sessionId : String(data['renderPaidSession'] || '') || (paidRunStarted(data) ? String(data['renderCheckoutSession']) : '');
    if (!paid) {
      throw new Error('pending');
    }
    if (data['renderPaidSession'] !== paid) {
      await updateDoc(reference, { renderEntitlement: 'full', renderPaidSession: paid });
    }
    return 'full';
  }

  // The pages read the name from `renderName` (jobFromDocument): an upload's document keeps the
  // new one.
  public override async rename(job: RenderJob, name: string): Promise<void> {
    if (job.source.kind === 'upload' && job.backendId) {
      await updateDoc(await this.modelDocument(job.backendId), { renderName: name });
    }
  }

  // As the library's stop (UploadFileService.abortCardRender): the backend stops the render, then
  // deletes the model, and its document goes — even when the stop or the delete fails, as the
  // library's does.
  public override async cancel(job: RenderJob): Promise<void> {
    if (job.source.kind === 'upload' && job.backendId) {
      await this.cards.stopRenderPostRequest(job.backendId).catch((error) => console.warn('Render flow: stop_render failed', error));
      await this.deleteModel(job.backendId);
      await deleteDoc(await this.modelDocument(job.backendId));
    }
  }

  // A finished preview taken out of the library (owner, 2026-10-22): the backend deletes the model,
  // and its document goes here at once — the library is that list, so the card goes on every tab and
  // device even when the backend's delete fails (docs/render-landing-port.md, decision 46).
  public override async discard(job: RenderJob): Promise<void> {
    if (job.source.kind === 'upload' && job.backendId) {
      await this.deleteModel(job.backendId);
      await deleteDoc(await this.modelDocument(job.backendId));
    }
  }

  // An upload's videos are on the backend; a sample's are the page's own.
  public override packStandIn(job: RenderJob): boolean {
    return job.source.kind !== 'upload' || !job.backendId;
  }

  // Both videos' sizes from their objects in the bucket; the backend answers 404 while either is
  // still on its way.
  public override async packSizes(job: RenderJob): Promise<RenderPackSizes | null> {
    if (this.packStandIn(job) || !job.backendId) {
      return super.packSizes(job);
    }
    const answer = await lastValueFrom(this.http.post<{ portrait?: { size_bytes?: number }; landscape?: { size_bytes?: number } }>(
      `${environment.endpoints.webBackend}/render_download_info`,
      { user_id: await this.firebaseAuth.getUserUid(), model_id: job.backendId },
      await this.backendOptions(),
    ));
    const portrait = Number(answer?.portrait?.size_bytes);
    const landscape = Number(answer?.landscape?.size_bytes);
    return portrait > 0 && landscape > 0 ? { portrait, landscape } : null;
  }

  // One ZIP, streamed by the backend in a POST: the browser cannot save a POST's answer as it comes,
  // so the page takes it whole (~3 MB a video) and hands it on as an object URL
  // (RenderPackService.saveFile). Each ticked cell is a file of its own, named for its platform.
  public override async getPack(job: RenderJob, keys: ReadonlyArray<RenderPackKey>): Promise<RenderPackFiles> {
    if (this.packStandIn(job) || !job.backendId) {
      return super.getPack(job, keys);
    }
    const items = keys.reduce<Array<{ platform: string; orientation: string }>>((list, key) => {
      const cell = renderPackCell(key);
      return cell ? [...list, { platform: cell.preset.platform, orientation: cell.orientation }] : list;
    }, []);
    const fileName = renderFileStem(job.name) || 'video';
    const zip = await lastValueFrom(this.http.post(
      `${environment.endpoints.webBackend}/render_download`,
      { user_id: await this.firebaseAuth.getUserUid(), model_id: job.backendId, file_name: fileName, items },
      { ...(await this.backendOptions('application/zip')), responseType: 'blob' },
    )).catch((error) => {
      console.error('Render flow: render_download failed', error);
      throw error;
    });
    return { files: [{ url: URL.createObjectURL(zip), name: `${fileName}.zip`, label: 'ZIP pack' }], standIn: false };
  }

  // del_model takes a preview-only model too since Yahor's 2af3213 (2026-10-23): its Spanner rows
  // (render_queue, preview_only_renders, spot_render_jobs among them), its results folder, its
  // document and the uploaded source. It answers at once and deletes after, so a failure there is
  // only in the backend's log; a refused request is only logged here.
  private async deleteModel(backendId: string): Promise<void> {
    await lastValueFrom(this.http.post(`${environment.endpoints.webBackend}/del_model`, { model_id: backendId }, await this.backendOptions()))
      .catch((error) => console.warn('Render flow: del_model failed', error));
  }

  // The render-flow models of whoever is signed in, live, as UploadFileService follows the library's
  // uploads after a sign-in: another tab, another device or a closed browser loses nothing.
  public override watchJobs(): Observable<RenderJob[]> {
    return new Observable<RenderJob[]>((subscriber) => {
      let uid: string | null = null;
      let stop: (() => void) | undefined;
      const follow = (next: string | null): void => {
        if (next === uid) {
          return;
        }
        stop?.();
        stop = undefined;
        uid = next;
        if (!next) {
          this.zone.run(() => subscriber.next([]));
          return;
        }
        const models = query(collection(this.firestore, `userdata/${next}/models`), where('renderProduct', '==', true));
        stop = onSnapshot(
          models,
          (snapshot) => this.zone.run(() => subscriber.next(snapshot.docs.map((document) => jobFromDocument(document.id, document.data())))),
          (error) => console.error('Render flow: the job list stopped', error),
        );
      };
      const signedIn = this.firebaseAuth.firebaseUser.subscribe((user) => follow(user?.uid || null));
      const signedOut = this.firebaseAuth.notLoggedUserSubscription.subscribe(() => follow(null));
      if (this.firebaseAuth.currentUser) {
        follow(this.firebaseAuth.currentUser.uid);
      }
      return () => {
        stop?.();
        signedIn.unsubscribe();
        signedOut.unsubscribe();
      };
    });
  }

  // An update, not a set: a document that is gone (a cancel) must not come back as a stub.
  public override async remember(job: RenderJob, facts: RenderJobFacts): Promise<void> {
    if (job.source.kind !== 'upload' || !job.backendId) {
      return;
    }
    const fields: Record<string, string> = {};
    if (facts.entitlement) {
      fields['renderEntitlement'] = facts.entitlement;
    }
    if (facts.seenStep) {
      fields['renderSeenStep'] = facts.seenStep;
    }
    if (Object.keys(fields).length) {
      await updateDoc(await this.modelDocument(job.backendId), fields).catch((error) => console.warn('Render flow: could not store', fields, error));
    }
  }

  // After a cold load the flow restores its jobs before Firebase has restored the session: the uid
  // is awaited, as the library's getFirebaseModelsData does.
  private async modelDocument(backendId: string): Promise<DocumentReference<DocumentData>> {
    const uid = await this.firebaseAuth.getUserUid();
    return doc(this.firestore, `userdata/${uid}/models/${backendId}`);
  }

  // The videos. Paid through Stripe, they are the webhook's to start: the page only marks them asked
  // for, so that a reload shows them under way before the renderer has said a word. A model nobody
  // paid for gets none ($9 for everyone, members too: owner, 2026-10-14). Nothing is ever started or
  // restarted from here (owner, 2026-10-20): failed videos go to support, not to a button.
  private async requestVideos(reference: DocumentReference<DocumentData>, data: ModelDocument | undefined): Promise<void> {
    if (!data || data['renderVideosRequested'] || PLATFORMS.every((platform) => status(data, platform) === 'finished')) {
      return;
    }
    if (!data['renderPaidSession'] && !paidRunStarted(data)) {
      throw new RenderRunError('These videos aren’t paid for yet. Go back to the preview to buy them.', 'account');
    }
    await updateDoc(reference, { renderVideosRequested: Date.now() });
  }

  private async backendOptions(accept = 'application/json'): Promise<{ headers: HttpHeaders }> {
    const token = await this.firebaseAuth.getUserToken();
    return { headers: new HttpHeaders({ Accept: accept, Authorization: `Bearer ${token}` }) };
  }

  // The model's document as a run: progress while the renderer works, the result once it is there,
  // an error when it fails, goes away, or stops changing for `stallMs` — a run that stops answering
  // is a failure the user sees, not an endless wait. Progress never goes back within a run.
  private follow<T>(
    backendId: string,
    stallMs: number,
    read: (data: ModelDocument) => FollowStep<T>,
    request?: (reference: DocumentReference<DocumentData>, data: ModelDocument | undefined) => Promise<void>,
  ): Observable<RenderRunEvent<T>> {
    return new Observable<RenderRunEvent<T>>((subscriber) => {
      let closed = false;
      let stop: (() => void) | undefined;
      let best = 0;
      let latest: ModelDocument | undefined;
      let signature = '';
      let changedAt = Date.now();
      let checkedAt = changedAt;
      const watchdog = this.zone.runOutsideAngular(() => setInterval(() => {
        const now = Date.now();
        if (now - checkedAt > STALL_ASLEEP_MS) {
          changedAt = now;
        }
        checkedAt = now;
        if (now - changedAt > stallMs) {
          // Kept on the document, so the failure outlives this page: the job list, the header, a
          // reload and another device all read it from there (failureOf).
          const stalled: StalledRun = { signature, minutes: Math.round(stallMs / 60000) };
          this.modelDocument(backendId)
            .then((reference) => updateDoc(reference, { renderStalled: stalled }))
            .catch((error) => console.warn('Render flow: could not store the stall', error));
          this.zone.run(() => subscriber.error(new RenderRunError(stallText(stalled.minutes, latest), 'service')));
        }
      }, STALL_CHECK_MS));

      (async () => {
        const reference = await this.modelDocument(backendId);
        if (request && !closed) {
          const current = await getDoc(reference);
          await request(reference, current.exists() ? current.data() : undefined);
        }
        if (closed) {
          return;
        }
        stop = onSnapshot(
          reference,
          (snapshot) => this.zone.run(() => {
            if (!snapshot.exists()) {
              subscriber.error(new RenderModelGoneError());
              return;
            }
            latest = snapshot.data();
            const next = runSignature(latest);
            if (next !== signature) {
              signature = next;
              changedAt = Date.now();
            }
            const step = read(latest);
            if ('error' in step) {
              subscriber.error(new RenderRunError(step.error.text, step.error.blame, step.error.about));
            } else if ('done' in step) {
              subscriber.next({ type: 'done', result: step.done });
              subscriber.complete();
            } else {
              best = Math.max(best, Math.min(99, Math.round(step.progress)));
              subscriber.next({ type: 'progress', progress: best, statusText: step.statusText, sides: step.sides, eta: step.eta });
            }
          }),
          (error) => this.zone.run(() => subscriber.error(error)),
        );
      })().catch((error) => this.zone.run(() => subscriber.error(error)));

      return () => {
        closed = true;
        clearInterval(watchdog);
        stop?.();
      };
    });
  }
}

// A render-flow document as the flow's job, read the way the library reads the same fields
// (UploadFileService.getAggregatedModelStatus): an error on either platform wins, and a platform
// that has finished waits for the other. Asked for, the videos are under way before the renderer
// has said a word about them.
function jobFromDocument(id: string, data: ModelDocument): RenderJob {
  const preview = framesOf(data);
  const statuses = PLATFORMS.map((platform) => status(data, platform));
  const landscapeVideo = mediaUrl(data['cardContentWeb']);
  const portraitVideo = mediaUrl(data['cardContentMobile']);
  const videos = landscapeVideo && portraitVideo && statuses.every((value) => value === 'finished') ? { landscape: landscapeVideo, portrait: portraitVideo } : undefined;
  const paid = !!data['renderPaidSession'] || paidRunStarted(data);
  const videoRun = !!preview && (!!data['renderVideosRequested'] || paid || statuses.some((value) => VIDEO_UNDER_WAY.includes(value) || value === 'inqueue' || value === 'podstarted'));
  const failure = failureOf(data, videoRun);

  let step: RenderFlowStep;
  let progress = 100;
  let statusText = '';
  let sides: RenderSides | undefined;
  let eta: RenderEtaReading | undefined;
  if (failure) {
    step = 'error';
    progress = 0;
  } else if (videos) {
    step = 'downloads';
    statusText = 'Videos ready';
  } else if (videoRun) {
    step = 'rendering';
    const read = readVideos(data);
    [progress, statusText] = 'progress' in read ? [read.progress, read.statusText] : [99, RENDER_VIDEO_LINES.finishing];
    eta = 'progress' in read ? read.eta : undefined;
  } else if (preview) {
    step = 'preview';
    statusText = 'Preview ready';
  } else {
    step = 'processing';
    const read = readPreview(data);
    [progress, statusText] = 'progress' in read ? [read.progress, read.statusText] : [99, `${RENDER_PREVIEW_STAGES[RENDER_PREVIEW_STAGES.length - 1].label}…`];
    sides = 'progress' in read ? read.sides : undefined;
  }

  const stored = (data['renderSource'] || {}) as Partial<Extract<RenderJobSource, { kind: 'upload' }>>;
  const createdAt = Number(data['renderCreatedAt']) || Number(data['modifiedDate']) * 1000 || 0;
  // The videos' failure only once they were asked for: frames alone do not make a failure the
  // videos' (a preview with a stray warning read as "Your videos stopped · Paid $9", 2026-10-02).
  const failedStep = videoRun ? 'rendering' : 'processing';
  return {
    modelId: id,
    backendId: id,
    name: String(data['renderName'] || removeTimestamp(String(data['cardTitle'] || id))),
    source: {
      kind: 'upload',
      fileName: stored.fileName || id,
      format: stored.format || '',
      fileCount: stored.fileCount || 1,
      textureCount: stored.textureCount || 0,
      totalBytes: stored.totalBytes || 0,
    },
    step,
    progress: step === 'processing' || step === 'rendering' ? Math.min(99, Math.round(progress)) : progress,
    statusText,
    sides,
    eta,
    // Paid, or its videos asked for (before Stripe, through the stand-in). `renderEntitlement` alone
    // is not enough: a member's upload got 'full' at once until 2026-10-14.
    entitlement: paid || !!data['renderVideosRequested'] ? 'full' : 'preview',
    ...(data['renderCheckoutSession'] ? { checkout: { sessionId: String(data['renderCheckoutSession']), at: Number(data['renderCheckoutAt']) || 0 } } : {}),
    preview,
    videos,
    error: failure
      ? { title: failedStep === 'rendering' ? 'We couldn’t finish the videos.' : 'We couldn’t prepare this preview.', text: failure.text, failedStep, blame: failure.blame, ...(failure.about ? { about: failure.about } : {}) }
      : undefined,
    // Something to look at that the user has not opened yet, on any device.
    unseen: (step === 'preview' || step === 'downloads' || step === 'error') && data['renderSeenStep'] !== step,
    createdAt,
    updatedAt: createdAt,
  };
}

// 'avocado1790610811193' → 'avocado' (UtilityFunctionsService.removeTimestampFromString).
function removeTimestamp(value: string): string {
  const match = value.match(/^(.*?)(\d{13})$/);
  return match && match[1] ? match[1] : value;
}

// The preview's two frames: the renderer's preview-only stills. The plain posters (`cardPoster*`)
// come only after a purchase (Yahor, 2026-10-23); they stand in only once a platform's run is past
// its frames — on the simulator's older documents, and after a full render that had no preview.
function framesOf(data: ModelDocument): RenderPreviewResult['preview'] | undefined {
  const [landscape, portrait] = PLATFORMS.map((platform) => frameOf(data, platform));
  if (!landscape || !portrait) {
    return undefined;
  }
  const [web, mobile] = PLATFORMS.map((platform) => mediaUrl(data[`previewOnlyPoster${platform}`]));
  return { landscape, portrait, landscapeCard: landscape, portraitCard: portrait, watermarked: { landscape: landscape === web, portrait: portrait === mobile } };
}

// The preview-only stills carry the preview's watermark and stay in the document after the purchase;
// the plain posters are the full render's, without one (Yahor, 2026-10-23: "как в обычной library"),
// and come only after a purchase: the paid run writes each at `thumbnailUploaded`, minutes before its
// video. Paid for, a plain poster is shown as soon as it is there (owner, 2026-11-02; the page blurs
// the watermarked one until then); otherwise only once its video is written.
function frameOf(data: ModelDocument, platform: Platform): string | null {
  const plain = mediaUrl(data[`cardPoster${platform}`]);
  if (plain && (videoWritten(data, platform) || paidFor(data))) {
    return plain;
  }
  return mediaUrl(data[`previewOnlyPoster${platform}`]) || (PAST_THE_FRAMES.includes(status(data, platform)) ? plain : null);
}

function videoWritten(data: ModelDocument, platform: Platform): boolean {
  return status(data, platform) === 'finished' && !!mediaUrl(data[`cardContent${platform}`]);
}

// Each orientation's phase, and its frame once that one is done: a done status whose frame is not
// written yet is still saving.
function sidesOf(data: ModelDocument): RenderSides {
  const phase = (platform: Platform): RenderSidePhase => {
    const value = PREVIEW_PHASES[status(data, platform)] || 'waiting';
    return value === 'done' && !frameOf(data, platform) ? 'saving' : value;
  };
  const sides: RenderSides = { landscape: phase('Web'), portrait: phase('Mobile') };
  if (sides.landscape === 'done') {
    sides.landscapeFrame = frameOf(data, 'Web') || undefined;
  }
  if (sides.portrait === 'done') {
    sides.portraitFrame = frameOf(data, 'Mobile') || undefined;
  }
  return sides;
}

// The two frames once both are there; until then where each orientation stands. The words are the
// stages card's (RENDER_PREVIEW_STAGES, renderPreviewStage): the user is told what is being done to
// the model, not which machine does it (owner, 2026-10-20).
function readPreview(data: ModelDocument): FollowStep<RenderPreviewResult> {
  const failure = failureOf(data, false);
  if (failure) {
    return { error: failure };
  }
  const preview = framesOf(data);
  if (preview) {
    return { done: { preview } };
  }
  // The bar's floor for where the renderer stands; the page creeps on from it (RenderFlowService).
  const sides = sidesOf(data);
  return { progress: renderPreviewFloor(sides), statusText: `${RENDER_PREVIEW_STAGES[renderPreviewStage(sides)].label}…`, sides };
}

// Both videos, once both platforms have finished.
function readVideos(data: ModelDocument): FollowStep<RenderVideosResult> {
  const failure = failureOf(data);
  if (failure) {
    return { error: failure };
  }
  const landscape = mediaUrl(data['cardContentWeb']);
  const portrait = mediaUrl(data['cardContentMobile']);
  if (landscape && portrait && PLATFORMS.every((platform) => status(data, platform) === 'finished')) {
    return { done: { videos: { landscape, portrait }, preview: framesOf(data) } };
  }
  // Each video said as it stands (owner, 2026-10-21): the line used to follow the slower one and
  // read "Setting up the camera path…" while the other was 83% rendered.
  const [landscapeSide, portraitSide] = PLATFORMS.map((platform) => videoSide(data, platform));
  return { progress: renderVideosProgress(landscapeSide, portraitSide), statusText: renderVideosStatus(landscapeSide.phase, portraitSide.phase), eta: etaOf(data) };
}

function etaOf(data: ModelDocument): RenderEtaReading {
  const startedAt = renderEtaNumber(data['renderVideosRequested']) || renderEtaNumber(data['renderCheckoutAt']);
  return { sides: PLATFORMS.map((platform) => etaSide(data, platform)), ...(startedAt ? { startedAt } : {}) };
}

// The frame fields are read only while a side's frames are under way: they are never reset (Yahor),
// so a second render starts with the last one's, and `videoSide` holds a side at 'preparing' until
// its progress moves. A progress that is no number is not that wait, and goes to the check too.
function etaSide(data: ModelDocument, platform: Platform): RenderEtaSide {
  const { phase } = videoSide(data, platform);
  if (phase === 'finishing' || phase === 'done') {
    return { state: 'finishing' };
  }
  const progress = data[`loadProgress${platform}`];
  return phase === 'rendering' || (status(data, platform) === 'thumbnailuploaded' && renderEtaNumber(progress) === null)
    ? renderEtaFrames(data[`framesTotal${platform}`], data[`currentFrame${platform}`], data[`avgFrameSec${platform}`], progress)
    : { state: 'waiting' };
}

function videoSide(data: ModelDocument, platform: Platform): RenderVideoSide {
  const value = status(data, platform);
  const reported = renderEtaNumber(data[`loadProgress${platform}`]);
  const progress = reported === null ? 0 : Math.min(100, Math.max(0, Math.trunc(reported)));
  let phase: RenderVideoPhase = 'waiting';
  if (videoWritten(data, platform)) {
    phase = 'done';
  } else if (VIDEO_FINISHING.includes(value)) {
    phase = 'finishing';
  } else if (value === 'thumbnailuploaded' && progress > VIDEO_FRAMES_FROM) {
    phase = 'rendering';
  } else if (VIDEO_PREPARING.includes(value)) {
    phase = 'preparing';
  }
  const frames = phase === 'rendering' ? Math.round(((progress - VIDEO_FRAMES_FROM) / (100 - VIDEO_FRAMES_FROM)) * 100) : 0;
  return { phase, frames };
}

// The renderer's and the backend's failure statuses (UploadFileService.handleModelProcessing), in
// the page's words, with whose failure each is. The backend turns down an account without a plan
// with `renderModelError` and the warning `checkSubscriptionError` (seen 2026-10-01): the warning
// decides, or the page would blame the textures. On a preview it is no failure at all
// (`planRefusalFails` false): the preview is free for every account, and the renderer goes on
// rendering it (its GPU jobs in FRAME_RENDERING) to `previewOnlyFinished`, the warning left on the
// document (the owner checked; the tester's previews, 2026-10-02). The page stopped those runs as
// refused until 2026-10-05. A preview that really never comes ends in the stall watchdog. For the
// videos it is the refusal it says (owner: the full render may require a plan).
function failureOf(data: ModelDocument, planRefusalFails = true): RenderFailure | null {
  const planRefused = (platform: Platform) => /error/.test(status(data, platform)) && plainText(data[`warning${platform}`]).trim().toLowerCase() === 'checksubscriptionerror';
  if (planRefusalFails && PLATFORMS.some(planRefused)) {
    return { text: 'The renderer turned this model down: your account has no active plan.', blame: 'account' };
  }
  // The page gave up on a silent renderer (follow): failed for as long as the renderer has said
  // nothing since.
  const stalled = data['renderStalled'] as StalledRun | null | undefined;
  if (stalled?.signature && stalled.signature === runSignature(data)) {
    return { text: stallText(stalled.minutes || 0, data), blame: 'service' };
  }
  for (const platform of PLATFORMS) {
    if (planRefused(platform)) {
      continue;
    }
    const warning = plainText(data[`warning${platform}`]).trim();
    switch (status(data, platform)) {
      case 'rendermodelerror': {
        const missing: string[] = Array.isArray(data['missingTextures']) ? data['missingTextures'] : [];
        const names = missing.map((path) => path.split('/').pop()).filter(Boolean);
        if (!names.length && warning) {
          return { text: `The renderer couldn’t read the model: ${warning}.`, blame: 'model' };
        }
        const text = names.length
          ? `Some textures are missing: ${names.slice(0, 3).join(', ')}${names.length > 3 ? ` and ${names.length - 3} more` : ''}. Upload the model with its textures folder, then try again.`
          : 'Some textures could not be read. Check that the model’s textures are included, then try again.';
        return { text, blame: 'model', about: 'textures' };
      }
      case 'generalerror':
        return { text: warning ? `The renderer stopped: ${warning}` : 'The renderer stopped with an error. Nothing is wrong with your model.', blame: 'service' };
      case 'unprocessablefiletype':
        return { text: 'This file can’t be rendered. Upload an FBX or USDZ model.', blame: 'model' };
      case 'parallellimiterror':
        return { text: 'Another of your models is rendering. Try again when it is done.', blame: 'account' };
      case 'triallimitreached':
      case 'usertrialexpirationerror':
      case 'userexpirationerror':
        return { text: 'Your account has no renders left. Check your plan, then try again.', blame: 'account' };
      case 'userrenderlimittriggered':
        return { text: 'You have reached your render limit. Contact support to raise it.', blame: 'account' };
      default:
        // A failure the page has no words for yet: said as the renderer said it, so whoever sees
        // it can pass it on.
        if (/error|limit|expir|fail/.test(status(data, platform))) {
          const raw = String(data[`loadStatus${platform}`]);
          return { text: `The renderer reported “${raw}”${warning ? ` (${warning})` : ''}. Try again, or contact support if it happens again.`, blame: 'service' };
        }
        break;
    }
  }
  return null;
}

// A checkout opened, and the renderer gone past the finished preview: Stripe's webhook has started
// the paid run, whether or not the buyer's return from Stripe reached this page.
function paidRunStarted(data: ModelDocument): boolean {
  return checkoutRunUnderWay(data) && !!framesOf(data);
}

// A checkout opened and a run past the preview's end: Stripe's webhook started the paid run.
function checkoutRunUnderWay(data: ModelDocument): boolean {
  return !!data['renderCheckoutSession'] && PLATFORMS.some((platform) => {
    const value = status(data, platform);
    return !!value && !PREVIEW_END.includes(value);
  });
}

// Paid for, as the frames read it: the checkout came back paid, or its run is under way. Not through
// paidRunStarted, which reads the frames.
function paidFor(data: ModelDocument): boolean {
  return !!data['renderPaidSession'] || checkoutRunUnderWay(data);
}

function status(data: ModelDocument, platform: Platform): string {
  return plainText(data[`loadStatus${platform}`]).toLowerCase();
}

// A text field as the renderer should have written it, nothing from anything else: a map in its
// place must not throw in the snapshot's callback.
function plainText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

// The library's documents start with 'undefined' (the string) for a missing video.
function mediaUrl(value: unknown): string | null {
  return typeof value === 'string' && value !== '' && value !== 'undefined' ? value : null;
}

// What counts as the renderer saying something: a status, a progress, a warning or a frame changing.
function runSignature(data: ModelDocument): string {
  return PLATFORMS.map((platform) => JSON.stringify([status(data, platform), data[`loadProgress${platform}`], data[`warning${platform}`], data[`currentFrame${platform}`]])).join('/');
}

// A run that stopped answering, with where each orientation stood, for a report.
function stallText(minutes: number, data: ModelDocument | undefined): string {
  const stood = (platform: Platform): string => (data && plainText(data[`loadStatus${platform}`])) || 'no status';
  return `The renderer hasn’t answered for ${minutes} minutes, so we stopped waiting (landscape: ${stood('Web')}, portrait: ${stood('Mobile')}). Try again, or contact support if it happens again.`;
}

// ' (HTTP 502)' for a failed request, nothing when it never got an answer.
function httpCode(error: unknown): string {
  return error instanceof HttpErrorResponse && error.status ? ` (HTTP ${error.status})` : '';
}

// 'Car/car.fbx' → 'Car/car1727000000000.fbx': the model's name carries the upload time
// (UtilityFunctionsService.modifyFileNameAndPathNotSafe), so two uploads of one file differ.
function stampModelPath(path: string): string {
  const slash = path.lastIndexOf('/');
  const name = path.slice(slash + 1);
  const extension = fileExtension(name);
  const stem = extension ? name.slice(0, name.length - extension.length) : name;
  return `${path.slice(0, slash + 1)}${stem}${Date.now()}${name.slice(stem.length)}`;
}

// 'uploaded_models/u/123_ab/Car/car1727.fbx' → 'uploaded_models/u/123_ab/Car': what POST /render
// takes as full_model_path (the backend lists the folder for the model's file).
function parentFolder(path: string): string {
  return path.slice(0, path.lastIndexOf('/'));
}

// The model's id: its file name without the extension, as the backend derives it from the path.
function baseName(path: string): string {
  const name = path.slice(path.lastIndexOf('/') + 1);
  const extension = fileExtension(name);
  return extension ? name.slice(0, name.length - extension.length) : name;
}
